import React, { useState, useContext, useEffect } from 'react';
import PortableText from 'react-portable-text';

import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Typography,
  Checkbox,
  ListItemText,
  makeStyles,
} from '@material-ui/core';

import {
  wavelyticsQuizPartOne,
  wavelyticsQuizFullControl,
  wavelyticsQuizMinimal,
} from '../../data/wavelyticsQuiz';

import PardotForm from '../ContactForm/PardotForm';

import FormContext from '../../context/FormContext';
import PardotContext from '../../context/PardotContext';

const DATA_ONLY = 'Data Factory';
const BENCHMARKING = 'Benchmarking + Advanced Analytics + Self Serve Analytics';
const NONBENCHMARKING = 'Advanced Analytics + Self Serve Analytics';

const FULL_CONTROL_RESPONSE =
  'High, we want full control over our data modeling and outputs';
const BENCHMARKING_RESPONSE =
  'To Compare Performance against industry benchmarks or customer trends';

const useStyles = makeStyles((theme) => ({
  introContainer: {
    padding: '0 4rem 4rem 4rem',
    minHeight: '275px',
    [theme.breakpoints.down('md')]: {
      padding: '0 2rem 2rem 2rem',
    },
  },
  introHeader: {
    // color: '#3FA529',
    marginBottom: '1rem',
    textAlign: 'left',
    lineHeight: 1.2,
    [theme.breakpoints.down('md')]: {
      lineHeight: 1.4,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.8rem',
      lineHeight: 1.2,
    },
  },
  introText: {
    textAlign: 'left',
    lineHeight: 1.7,
    fontWeight: 400,
    color: '#4B5B69',
    [theme.breakpoints.down('md')]: {
      lineHeight: 1.5,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.2rem',
      lineHeight: 1.4,
    },
  },
  quizContainer: {
    padding: '3rem',
    [theme.breakpoints.down('md')]: {
      padding: '2rem',
    },
  },
  menuItem: {
    [theme.breakpoints.down('md')]: {
      whiteSpace: 'normal',
    },
  },
  questionContainer: {
    minHeight: '85px',
    [theme.breakpoints.down('xs')]: {
      minHeight: '200px',
    },
  },
  question: {
    // color: '#3FA529',
    textAlign: 'left',
    fontSize: '3rem',
    lineHeight: 1.2,
    [theme.breakpoints.down('md')]: {
      fontSize: '2.5rem',
      lineHeight: 1.4,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.8rem',
      lineHeight: 1.2,
    },
  },
  quizPrompt: {
    marginBottom: '15px',
    color: '#4B5B69',
    [theme.breakpoints.down('md')]: {
      lineHeight: 1.4,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.8rem',
      lineHeight: 1.2,
    },
  },
  prevButton: {
    '&:hover': {
      backgroundColor: '#2A7ABC',
      color: 'white',
      opacity: 1,
    },
  },
}));

export const WavelyticsQuiz = ({
  setQuizCompleted,
  quizStarted,
  setQuizStarted,
  quizIntro,
  pardotUrl,
  quizCompleted,
}) => {
  const classes = useStyles();
  const [step, setStep] = useState(0);
  const [activeQuiz, setActiveQuiz] = useState({
    quiz: wavelyticsQuizPartOne,
    id: 0,
  });
  const [endedEarly, setEndedEarly] = useState(false);

  const {
    wavelyticsAnswers,
    handleWavelyticsAnswers,
    setRecommendation,
    recommendation,
  } = useContext(FormContext);
  const { setWavelyticsParsedAnswers } = useContext(PardotContext);

  const isMulti = activeQuiz.id === 2 && step === 0;
  const currentStep = activeQuiz.id === 0 ? step : step + 3;
  const rawAnswer = wavelyticsAnswers.find(
    (a) => a.step === currentStep
  )?.answer;

  useEffect(() => {
    //checks to see if the quiz is completed, parses responses, trigger to show form
    if (!activeQuiz.quiz[step] || endedEarly) {
      handleQuizCompleted();
    }
  }, [activeQuiz.quiz[step], endedEarly]);

  //determines if the input should be a multi select or a single select
  const selectValue = isMulti
    ? Array.isArray(rawAnswer)
      ? rawAnswer
      : []
    : typeof rawAnswer === 'string'
    ? rawAnswer
    : '';

  const handleSetStep = () => {
    const currentAnswer = wavelyticsAnswers[currentStep]?.answer;

    // End of part one – determine path
    if (activeQuiz.id === 0 && step === wavelyticsQuizPartOne.length - 1) {
      const controlAnswer = wavelyticsAnswers.find((a) => a.step === 2)?.answer;

      const isFullControl = controlAnswer === FULL_CONTROL_RESPONSE;

      setActiveQuiz({
        quiz: isFullControl ? wavelyticsQuizFullControl : wavelyticsQuizMinimal,
        id: isFullControl ? 1 : 2,
      });
      setStep(0);
      return;
    }

    // Full control quiz completed – recommend data-only
    if (activeQuiz.id === 1) {
      setRecommendation(DATA_ONLY);
    }

    // Early exit conditions for minimal quiz
    if (activeQuiz.id === 2) {
      if (
        step === 0 &&
        Array.isArray(currentAnswer) &&
        BENCHMARKING_RESPONSE.includes(currentAnswer)
      ) {
        setEndedEarly(true);
        setRecommendation(BENCHMARKING);
        return;
      }

      if (step === 1) {
        if (currentAnswer === 'Yes') {
          setEndedEarly(true);
          setRecommendation(BENCHMARKING);
          return;
        } else if (currentAnswer === 'No') {
          setEndedEarly(true);
          setRecommendation(NONBENCHMARKING);
          return;
        }
      }
    }

    // Advance step by default
    setStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    setStep((prev) => prev - 1);
  };

  const handleQuizCompleted = () => {
    //parses answers, sets utm
    setQuizCompleted(true);
    const parsedAnswer = wavelyticsAnswers
      .map(
        (answer) =>
          `${answer.question}: ${
            Array.isArray(answer.answer)
              ? answer.answer.join(', ')
              : answer.answer
          }`
      )
      .join(' | ');
    setWavelyticsParsedAnswers(
      parsedAnswer + ` | Suggested Solution: ${recommendation}`
    );
  };

  if (!quizStarted) {
    //shows intro if quiz isn't started yet
    return (
      <Grid
        container
        className={classes.introContainer}
        justifyContent='flex-start'
      >
        <PortableText
          content={quizIntro}
          serializers={{
            h2: ({ children }) => (
              <Grid item lg={8}>
                <Typography
                  variant='h1'
                  component='h2'
                  className={classes.introHeader}
                  color='primary'
                >
                  {children}
                </Typography>
              </Grid>
            ),
            normal: ({ children }) => (
              <Typography
                variant='h5'
                color='primary'
                className={classes.introText}
              >
                {children}
              </Typography>
            ),
          }}
        />
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={() => {
            setQuizStarted(true);
            setStep(0);
          }}
          style={{ marginTop: '2rem' }}
        >
          Start Quiz
        </Button>
      </Grid>
    );
  }

  if (!activeQuiz.quiz[step] || endedEarly) {
    //shows form on completion of quiz. checks if no active step or if ended early
    return (
      <div style={{ padding: '0rem 1rem' }}>
        <Typography variant='h3' className={classes.quizPrompt}>
          Thanks for taking our quiz. Fill out the form to get your results.
        </Typography>
        <PardotForm url={pardotUrl} />
      </div>
    );
  }

  //trigger for unanswered questions to disable next button
  const unanswered = !wavelyticsAnswers[currentStep]?.answer && !endedEarly;
  const firstQuestion = activeQuiz.id === 0 && step === 0;
  return (
    <form className={classes.quizContainer}>
      <Grid container spacing={2} justifyContent='center'>
        <Grid item xs={12} className={classes.questionContainer}>
          <Typography variant='h3' color='primary' className={classes.question}>
            {activeQuiz.quiz[step].question}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id='wavelytics-select-label'>
              {isMulti ? 'Select All That Apply' : 'Select An Option'}
            </InputLabel>
            <Select
              labelId='wavelytics-select-label'
              multiple={isMulti}
              value={selectValue}
              onChange={(e) =>
                handleWavelyticsAnswers(
                  e.target.value,
                  step,
                  activeQuiz,
                  activeQuiz.quiz[step].question
                )
              }
              renderValue={(selected) =>
                isMulti ? selected.join(', ') : selected
              }
            >
              {activeQuiz.quiz[step].answers.map((option) => (
                <MenuItem
                  key={option.id ?? option}
                  value={option.answer ?? option}
                  className={classes.menuItem}
                >
                  {isMulti && (
                    <Checkbox
                      checked={selectValue.includes(option.answer ?? option)}
                    />
                  )}
                  <ListItemText primary={option.answer ?? option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          container
          justifyContent={firstQuestion ? 'flex-end' : 'space-between'}
        >
          {!firstQuestion ? (
            //only show previous button for questions after the first
            <Button
              variant='outlined'
              color='secondary'
              size='large'
              onClick={handlePrev}
              style={{ marginTop: '2rem' }}
              className={classes.prevButton}
            >
              Previous
            </Button>
          ) : null}

          <Button
            variant='contained'
            color='primary'
            size='large'
            onClick={
              activeQuiz.quiz[step] === activeQuiz.quiz.length - 1 || endedEarly
                ? handleQuizCompleted
                : handleSetStep
            }
            style={{ marginTop: '2rem' }}
            disabled={unanswered}
          >
            Next
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
